import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaPassport,
  FaUsers,
  FaGlobe,
  FaCheckCircle,
  FaFileAlt,
  FaHandshake,
  FaPlaneDeparture,
  FaWhatsapp,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaArrowRight,
  FaBriefcase,
  FaGraduationCap,
  FaBuilding,
  FaChartLine,
  FaShieldAlt,
  FaClock,
  FaAward,
  FaHeart,
} from "react-icons/fa";
import CountUp from "react-countup";

const TEAM_VALUES = [
  {
    Icon: FaHeart,
    title: "Client-First Approach",
    desc: "Every decision we make is guided by what's best for our clients. We treat each case with the same personal care and attention.",
    color: "bg-red-50 text-red-500",
  },
  {
    Icon: FaShieldAlt,
    title: "Integrity & Transparency",
    desc: "No hidden fees, no false promises. We're honest with our clients about every step — even when it's not what they want to hear.",
    color: "bg-blue-50 text-blue-600",
  },
  {
    Icon: FaAward,
    title: "Excellence in Service",
    desc: "Our consultants stay up-to-date with immigration law changes worldwide to ensure every application has the best possible chance of success.",
    color: "bg-yellow-50 text-yellow-500",
  },
];

const WHY_CHOOSE = [
  {
    Icon: FaPassport,
    title: "Visa Specialists",
    desc: "Extensive experience with visa applications across multiple countries with a team of certified immigration consultants.",
  },
  {
    Icon: FaUsers,
    title: "Personalised Guidance",
    desc: "Every client is assigned a dedicated consultant who manages their case from start to finish.",
  },
  {
    Icon: FaGlobe,
    title: "Global Reach",
    desc: "We handle immigration for 16+ countries — from Canada and the UK to Singapore and the UAE.",
  },
  {
    Icon: FaChartLine,
    title: "98% Success Rate",
    desc: "Our meticulous approach and attention to detail delivers visa results that speak for themselves.",
  },
  {
    Icon: FaClock,
    title: "Fast Processing",
    desc: "We work efficiently to minimise delays, ensuring your application is filed accurately and on time.",
  },
  {
    Icon: FaFileAlt,
    title: "Document Experts",
    desc: "From attestation to trade licenses, our team handles all document clearing and legalisation services.",
  },
];

const SERVICES_OFFERED = [
  { Icon: FaBriefcase, label: "Work Visas", slug: "work-visa" },
  { Icon: FaGraduationCap, label: "Student Visas", slug: "student-visa" },
  { Icon: FaPlaneDeparture, label: "Travel Visas", slug: "travel-visa" },
  { Icon: FaBuilding, label: "Business Visas", slug: "business-visa" },
  { Icon: FaFileAlt, label: "Attestation", slug: "attestation" },
  { Icon: FaBuilding, label: "Trade License", slug: "trade-license" },
];

export default function About() {
  return (
    <div>
      <Helmet>
        <title>About Us | Lumis Documents Clearing Dubai</title>
        <meta name="description" content="Learn about Lumis Documents Clearing, Dubai's trusted immigration experts. Over 10 years of experience helping hundreds of clients achieve their global dreams." />
      </Helmet>
      {/* ── HERO ── */}
      <section
        className="h-[420px] flex items-center justify-center text-white bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2068&auto=format&fit=crop')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center px-6">
          <p className="tracking-widest text-blue-400 text-sm mb-3 uppercase">
            Who We Are
          </p>

          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            About Lumis Documents Clearing
          </h1>

          <p className="text-gray-200 max-w-xl mx-auto mb-5">
            A trusted immigration consultancy based in Dubai, dedicated to
            helping individuals, students and businesses reach their global
            destinations.
          </p>

          <div className="text-gray-300 text-sm">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white font-medium">About</span>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="bg-blue-600 text-white py-10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { end: 1200, suffix: "+", label: "Visas Processed" },
            { end: 800, suffix: "+", label: "Happy Clients" },
            { end: 98, suffix: "%", label: "Success Rate" },
            { end: 16, suffix: "+", label: "Countries Covered" },
          ].map(({ end, suffix, label }) => (
            <div key={label}>
              <h3 className="text-3xl font-bold">
                <CountUp
                  end={end}
                  duration={2.5}
                  enableScrollSpy
                  scrollSpyDelay={200}
                />
                {suffix}
              </h3>
              <p className="text-blue-100 text-sm mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── COMPANY STORY ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* IMAGE */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=2069&auto=format&fit=crop"
                alt="Lumis Documents Clearing office"
                className="rounded-2xl shadow-2xl w-full object-cover h-[420px]"
              />
              {/* FLOATING BADGE */}
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white rounded-2xl p-6 shadow-xl hidden md:block">
                <p className="text-3xl font-bold">10+</p>
                <p className="text-blue-200 text-sm mt-1">Years of Experience</p>
              </div>
            </div>

            {/* TEXT */}
            <div>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
                Our Story
              </p>

              <h2 className="text-3xl font-bold mb-6 leading-tight">
                Trusted Immigration Experts Based in Dubai
              </h2>

              <p className="text-gray-600 mb-5 leading-relaxed">
                Lumis Documents Clearing is a professional immigration
                consultancy headquartered at the Lake Central Tower in Business
                Bay, Dubai. Founded with a mission to simplify global
                immigration, we have helped over 800 clients reach their
                international destinations through expert visa guidance and
                document clearing services.
              </p>

              <p className="text-gray-600 mb-8 leading-relaxed">
                Our team of certified consultants brings deep knowledge of
                immigration policies across 16+ countries. Whether you're a
                professional seeking a work permit, a student planning to study
                abroad, or a business expanding globally — we have the expertise
                to guide you through every step.
              </p>

              <div className="flex flex-col gap-3 mb-8">
                {[
                  "Certified immigration consultants",
                  "Transparent fee structure with no hidden costs",
                  "Dedicated consultant assigned to every client",
                  "End-to-end support from consultation to approval",
                ].map((point) => (
                  <div key={point} className="flex items-center gap-3 text-sm text-gray-700">
                    <FaCheckCircle className="text-blue-600 flex-shrink-0" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700 transition shadow-md"
                >
                  Get Consultation <FaArrowRight />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 border border-blue-600 text-blue-600 px-7 py-3 rounded-full font-semibold hover:bg-blue-50 transition"
                >
                  Our Services <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CORE VALUES ── */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Our Core Values
          </h2>
          <p className="text-center text-gray-500 mb-14">
            The principles that guide everything we do
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {TEAM_VALUES.map(({ Icon, title, desc, color }) => (
              <div
                key={title}
                className="group bg-white rounded-2xl p-10 shadow hover:shadow-2xl hover:-translate-y-2 transition duration-300 text-center border border-gray-100"
              >
                <div
                  className={`w-16 h-16 ${color} rounded-full flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition duration-300`}
                >
                  <Icon size={26} />
                </div>
                <h3 className="font-bold text-xl mb-3 group-hover:text-blue-600 transition">
                  {title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* TEXT SIDE */}
            <div>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
                Why Lumis
              </p>
              <h2 className="text-3xl font-bold mb-6 leading-tight">
                Why Hundreds of Clients Choose Us
              </h2>
              <p className="text-gray-500 mb-10 leading-relaxed">
                We combine expert knowledge, a personalised approach and a
                transparent process to deliver immigration results that other
                agencies can't match.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {WHY_CHOOSE.map(({ Icon, title, desc }) => (
                  <div
                    key={title}
                    className="group flex gap-4 items-start"
                  >
                    <div className="w-11 h-11 bg-blue-50 group-hover:bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 transition duration-300">
                      <Icon className="text-blue-600 group-hover:text-white text-lg transition duration-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1 text-sm group-hover:text-blue-600 transition">
                        {title}
                      </h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* IMAGE SIDE */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop"
                alt="Lumis team consultation"
                className="rounded-2xl shadow-2xl w-full object-cover h-[480px]"
              />
              {/* OVERLAY STAT */}
              <div className="absolute top-6 -left-6 bg-white rounded-2xl shadow-xl p-5 hidden md:flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                  <FaAward className="text-blue-600 text-2xl" />
                </div>
                <div>
                  <p className="font-bold text-xl text-gray-800">98%</p>
                  <p className="text-xs text-gray-500">Visa Success Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR PROCESS ── */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">Our Process</h2>
          <p className="text-center text-gray-500 mb-14">
            A simple, transparent journey from your first enquiry to visa approval
          </p>

          <div className="grid md:grid-cols-4 gap-8 text-center relative">
            {/* CONNECTING LINE */}
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-blue-100 z-0" />

            {[
              { step: "01", Icon: FaPhoneAlt, title: "Free Consultation", desc: "Book a free consultation. We assess your profile and identify the best visa pathway." },
              { step: "02", Icon: FaFileAlt, title: "Document Collection", desc: "We give you a precise checklist and verify every document before submission." },
              { step: "03", Icon: FaHandshake, title: "Application Filing", desc: "We submit your application and manage all communications with authorities." },
              { step: "04", Icon: FaPlaneDeparture, title: "Visa Approved!", desc: "Receive your visa and start your international journey with confidence." },
            ].map(({ step, Icon, title, desc }) => (
              <div key={step} className="relative z-10">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-5 shadow-lg">
                  {step}
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition">
                  <Icon className="text-blue-600 text-xl mx-auto mb-3" />
                  <h3 className="font-bold text-base mb-2">{title}</h3>
                  <p className="text-gray-500 text-sm">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES WE OFFER ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Services We Offer
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Comprehensive immigration and document clearing services worldwide
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {SERVICES_OFFERED.map(({ Icon, label, slug }) => (
              <Link
                key={slug}
                to={`/services/${slug}`}
                className="group bg-white border border-gray-100 rounded-xl p-6 text-center shadow hover:shadow-xl hover:-translate-y-2 transition duration-300"
              >
                <div className="w-12 h-12 bg-blue-50 group-hover:bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3 transition duration-300">
                  <Icon className="text-blue-600 group-hover:text-white text-xl transition duration-300" />
                </div>
                <p className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition">
                  {label}
                </p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition shadow-md"
            >
              View All Services <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ── COMPANY STATISTICS ── */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Our Track Record
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Numbers that reflect our commitment to client success
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { Icon: FaPassport, end: 1200, suffix: "+", label: "Visas Processed", color: "bg-blue-50 text-blue-600" },
              { Icon: FaUsers, end: 800, suffix: "+", label: "Happy Clients", color: "bg-green-50 text-green-600" },
              { Icon: FaChartLine, end: 98, suffix: "%", label: "Success Rate", color: "bg-yellow-50 text-yellow-500" },
              { Icon: FaGlobe, end: 16, suffix: "+", label: "Countries Covered", color: "bg-purple-50 text-purple-600" },
            ].map(({ Icon, end, suffix, label, color }) => (
              <div
                key={label}
                className="bg-white rounded-2xl p-8 shadow-md text-center hover:shadow-xl hover:-translate-y-2 transition duration-300"
              >
                <div className={`w-14 h-14 ${color} rounded-full flex items-center justify-center mx-auto mb-5`}>
                  <Icon size={22} />
                </div>
                <h3 className="text-4xl font-bold text-gray-800 mb-1">
                  <CountUp
                    end={end}
                    duration={2.5}
                    enableScrollSpy
                    scrollSpyDelay={200}
                  />
                  {suffix}
                </h3>
                <p className="text-gray-500 text-sm">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OFFICE / LOCATION ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* MAP */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-100">
              <iframe
                title="Lumis Office"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.5!2d55.264!3d25.186!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f69d1be1a5a3d%3A0x5e54e6b4d12a9f5!2sLake+Central+Tower+Business+Bay!5e0!3m2!1sen!2sae!4v1680000000000!5m2!1sen!2sae"
                width="100%"
                height="360"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* CONTACT / OFFICE INFO */}
            <div>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
                Find Us
              </p>
              <h2 className="text-3xl font-bold mb-6">Visit Our Office</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                We welcome clients to visit our Dubai office for an in-person
                consultation. Our team is available Monday to Friday during
                business hours.
              </p>

              <div className="flex flex-col gap-5">
                {[
                  { Icon: FaMapMarkerAlt, label: "Address", value: "Lake Central Tower, Business Bay, Dubai, UAE", color: "bg-blue-50 text-blue-600", href: null },
                  { Icon: FaPhoneAlt, label: "Phone", value: "+971 54 766 2665", color: "bg-green-50 text-green-600", href: "tel:+971547662665" },
                  { Icon: FaClock, label: "Working Hours", value: "Mon – Fri: 9:30 AM – 6:30 PM", color: "bg-orange-50 text-orange-500", href: null },
                ].map(({ Icon, label, value, color, href }) => (
                  <div key={label} className="flex items-center gap-5">
                    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-0.5">{label}</p>
                      {href ? (
                        <a href={href} className="font-semibold text-gray-800 hover:text-blue-600 transition text-sm">
                          {value}
                        </a>
                      ) : (
                        <p className="font-semibold text-gray-800 text-sm">{value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 mt-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700 transition shadow-md"
                >
                  Contact Us <FaArrowRight />
                </Link>
                <a
                  href="https://wa.me/971547662665"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-7 py-3 rounded-full font-semibold transition shadow-md"
                >
                  <FaWhatsapp size={18} /> WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="bg-blue-600 text-white py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">
          Start Your Immigration Journey Today
        </h2>
        <p className="mb-8 text-blue-100 max-w-xl mx-auto">
          Speak with our expert consultants for a free initial assessment.
          We'll guide you to the visa pathway that's right for you.
        </p>

        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/contact"
            className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition shadow-md"
          >
            Get Free Consultation
          </Link>
          <Link
            to="/countries"
            className="border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition"
          >
            Explore Countries
          </Link>
        </div>
      </section>

      {/* ── FLOATING WIDGET ── */}
      <div className="fixed bottom-6 right-6 flex flex-col items-end gap-4 z-50">
        <div className="bg-white shadow-xl rounded-lg p-4 max-w-xs animate-bounce">
          <p className="text-sm text-gray-700">💬 Need help with your visa?</p>
          <p className="text-sm font-semibold text-gray-900">
            Chat with us on WhatsApp
          </p>
        </div>

        <Link to="/contact" className="group flex items-center">
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Free Consultation
          </span>
          <div className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaPhoneAlt size={20} />
          </div>
        </Link>

        <a
          href="https://wa.me/971547662665"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center"
        >
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Chat on WhatsApp
          </span>
          <div className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaWhatsapp size={22} />
          </div>
        </a>
      </div>
    </div>
  );
}
